import { getWindowOrigin } from '#pplx/frontend/common/location.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { isProduction as isProductionHost, isStaging as isStagingHost, } from '#pplx/frontend/platform-components/env.js';
import { clearServiceWorkerHtmlCache } from '#pplx/frontend/workers/service-worker/utils/clearServiceWorkerRuntimeCache.js';
import { isServiceWorkerSupported } from '#pplx/frontend/workers/service-worker/utils/isServiceWorkerSupported.js';
const ROOT_SERVICE_WORKER_LOCATION = { scriptPath: '/service-worker.js', scope: '/' };
const TEAMMATE_PATH_PREFIX = '/tm';
// The Teammate app previously lived under these scopes; the core app now owns `/teammate/:uuid`.
const LEGACY_TEAMMATE_SCOPE_PATHS = ['/nova-app', '/teammate'];
function isTeammatePath(pathname) {
    return pathname === TEAMMATE_PATH_PREFIX || pathname.startsWith(`${TEAMMATE_PATH_PREFIX}/`);
}
function isLegacyTeammatePath(pathname) {
    return pathname === '/teammate' || pathname.startsWith('/teammate/');
}
function getAppServiceWorkerLocation(pathname) {
    if (isTeammatePath(pathname)) {
        return { scriptPath: '/teammate-service-worker.js', scope: TEAMMATE_PATH_PREFIX };
    }
    return null;
}
function getServiceWorkerLocations(pathname) {
    const appWorker = getAppServiceWorkerLocation(pathname);
    return appWorker ? [ROOT_SERVICE_WORKER_LOCATION, appWorker] : [ROOT_SERVICE_WORKER_LOCATION];
}
function shouldDisableDocumentCachingForHiddenStaging({ serverEnvironment, }) {
    const clientHostname = typeof window === 'undefined' ? '' : window.location.hostname;
    return (serverEnvironment === 'staging' &&
        isProductionHost(clientHostname) &&
        !isStagingHost(clientHostname));
}
function isInvalidDocumentStateError(error) {
    return (error instanceof DOMException &&
        error.name === 'InvalidStateError' &&
        error.message === 'Failed to register a ServiceWorker: The document is in an invalid state.');
}
function isLegacyNovaAppRegistration(registration) {
    const documentOrigin = getWindowOrigin({ intent: 'document' });
    try {
        const scope = new URL(registration.scope, documentOrigin);
        return (scope.origin === documentOrigin &&
            LEGACY_TEAMMATE_SCOPE_PATHS.some((path) => scope.pathname === path || scope.pathname === `${path}/`));
    }
    catch {
        return false;
    }
}
async function unregisterLegacyNovaAppServiceWorkers() {
    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations
            .filter(isLegacyNovaAppRegistration)
            .map((registration) => registration.unregister()));
    }
    catch (error) {
        logger.error('Error unregistering legacy nova-app service worker', error);
    }
}
/**
 * Registers the service worker.
 */
export async function registerServiceWorker({ serverEnvironment, version, } = {}) {
    if (!isServiceWorkerSupported())
        return;
    // Hidden staging serves staging HTML on the production host. Reusing the
    // production service worker document cache can replay the wrong shell on
    // reload, so disable document caching for that split-brain environment.
    const disableDocumentCaching = shouldDisableDocumentCachingForHiddenStaging({
        serverEnvironment,
    });
    if (disableDocumentCaching) {
        await clearServiceWorkerHtmlCache();
    }
    if (isTeammatePath(window.location.pathname) || isLegacyTeammatePath(window.location.pathname)) {
        await unregisterLegacyNovaAppServiceWorkers();
    }
    const { Workbox } = await import('workbox-window');
    await Promise.all(getServiceWorkerLocations(window.location.pathname).map(async ({ scriptPath, scope }) => {
        const url = new URL(scriptPath, getWindowOrigin({ intent: 'document' }));
        if (version) {
            url.searchParams.set('v', version);
        }
        if (disableDocumentCaching) {
            url.searchParams.set('xhtml', '1');
        }
        if (import.meta.env.VITE_WEB_RESOURCES_BUILD || import.meta.env.VITE_SW_ENABLED_LOCAL) {
            url.searchParams.set('xc', '1');
        }
        const wb = new Workbox(url.toString(), import.meta.env.VITE_SW_ENABLED_LOCAL ? { type: 'module', scope } : { scope });
        try {
            await wb.register();
        }
        catch (error) {
            if (isInvalidDocumentStateError(error))
                return;
            logger.error('Error registering service worker', error);
        }
    }));
}
/**
 * Unregisters the service worker.
 */
export async function unregisterServiceWorker() {
    if (!isServiceWorkerSupported())
        return;
    await navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())));
}
//# sourceMappingURL=index.js.map